import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Textarea } from '../ui'
import {
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  UserIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  DocumentCheckIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { formatDate } from '../../utils/formatters'
import { ServiceProviderDocument, DocumentStatus, ServiceType } from '../../types'
import DocumentPreviewModal from './DocumentPreviewModal'
import { API_BASE } from '../../services/api'

const API_BASE_URL = API_BASE

// Document requirements configuration (same as registration)
const serviceDocumentRequirements = {
  // Service types (from registration)
  AMBULANCE_TRANSPORT: {
    required: ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE', 'INSURANCE_DOCUMENT', 'IDENTIFICATION'],
    optional: ['OPERATING_PERMIT', 'VEHICLE_REGISTRATION'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      PROFESSIONAL_CERTIFICATE: 'Medical/Emergency Services Certification',
      INSURANCE_DOCUMENT: 'Vehicle and Liability Insurance',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      OPERATING_PERMIT: 'Transport Operating Permit',
      VEHICLE_REGISTRATION: 'Ambulance Vehicle Registration Documents'
    }
  },
  GHUSL_SERVICE: {
    required: ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE', 'IDENTIFICATION'],
    optional: ['ISLAMIC_QUALIFICATION', 'CHARACTER_REFERENCE'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      PROFESSIONAL_CERTIFICATE: 'Ghusl Services Certification',
      INSURANCE_DOCUMENT: 'Professional Liability Insurance (Optional)',
      IDENTIFICATION: 'IC/Passport',
      ISLAMIC_QUALIFICATION: 'Islamic Qualification Certificates',
      CHARACTER_REFERENCE: 'Character Reference Letters',
      OPERATING_PERMIT: 'Religious Services Operating Permit'
    }
  },
  KAFAN_SERVICE: {
    required: ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE', 'IDENTIFICATION'],
    optional: ['ISLAMIC_QUALIFICATION', 'CHARACTER_REFERENCE'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      PROFESSIONAL_CERTIFICATE: 'Kafan Services Certification',
      INSURANCE_DOCUMENT: 'Professional Liability Insurance (Optional)',
      IDENTIFICATION: 'IC/Passport',
      ISLAMIC_QUALIFICATION: 'Islamic Qualification Certificates',
      CHARACTER_REFERENCE: 'Character Reference Letters'
    }
  },
  JANAZAH_LEAD: {
    required: ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE', 'IDENTIFICATION'],
    optional: ['ISLAMIC_QUALIFICATION', 'CHARACTER_REFERENCE'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      PROFESSIONAL_CERTIFICATE: 'Janazah Leadership Certification',
      INSURANCE_DOCUMENT: 'Professional Liability Insurance (Optional)',
      IDENTIFICATION: 'IC/Passport',
      ISLAMIC_QUALIFICATION: 'Islamic Qualification Certificates',
      CHARACTER_REFERENCE: 'Character Reference Letters'
    }
  },
  BURIAL_PLOT: {
    required: ['BUSINESS_LICENSE', 'OPERATING_PERMIT', 'IDENTIFICATION'],
    optional: ['LAND_OWNERSHIP', 'ENVIRONMENTAL_CLEARANCE'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      PROFESSIONAL_CERTIFICATE: 'Burial Services Management Certification (Optional)',
      INSURANCE_DOCUMENT: 'Public Liability Insurance (Optional)',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      OPERATING_PERMIT: 'Burial Plot Operating Permit',
      LAND_OWNERSHIP: 'Land Ownership or Lease Documents',
      ENVIRONMENTAL_CLEARANCE: 'Environmental Clearance Certificate'
    }
  },
  CATERING: {
    required: ['BUSINESS_LICENSE', 'FOOD_HANDLER_CERT', 'IDENTIFICATION'],
    optional: ['HALAL_CERTIFICATION', 'KITCHEN_FACILITY_LICENSE'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      FOOD_HANDLER_CERT: 'Food Handler Certificate',
      IDENTIFICATION: 'IC/Passport of Food Handlers',
      HALAL_CERTIFICATION: 'Halal Certification',
      KITCHEN_FACILITY_LICENSE: 'Kitchen Facility License',
      PROFESSIONAL_CERTIFICATE: 'Food Handler Certificate',
      INSURANCE_DOCUMENT: 'Public Liability Insurance (Optional)',
      OPERATING_PERMIT: 'Food Business Operating Permit'
    }
  },
  FLOWERS: {
    required: ['BUSINESS_LICENSE', 'IDENTIFICATION'],
    optional: ['SUPPLIER_CONTRACTS', 'QUALITY_CERTIFICATIONS'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      SUPPLIER_CONTRACTS: 'Flower Supplier Contracts',
      QUALITY_CERTIFICATIONS: 'Quality Assurance Certifications',
      INSURANCE_DOCUMENT: 'Public Liability Insurance (Optional)'
    }
  },
  TENT_RENTAL: {
    required: ['BUSINESS_LICENSE', 'IDENTIFICATION'],
    optional: ['EQUIPMENT_REGISTRATION', 'SAFETY_CERTIFICATIONS'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      EQUIPMENT_REGISTRATION: 'Equipment Registration Documents',
      SAFETY_CERTIFICATIONS: 'Safety Compliance Certifications',
      INSURANCE_DOCUMENT: 'Public Liability Insurance (Optional)'
    }
  },
  PHOTOGRAPHY: {
    required: ['BUSINESS_LICENSE', 'IDENTIFICATION'],
    optional: ['PORTFOLIO', 'PROFESSIONAL_CERTIFICATIONS'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      PORTFOLIO: 'Photography Portfolio',
      PROFESSIONAL_CERTIFICATIONS: 'Photography Professional Certifications',
      INSURANCE_DOCUMENT: 'Professional Liability Insurance (Optional)'
    }
  },
  YASIN_BOOKS: {
    required: ['BUSINESS_LICENSE', 'IDENTIFICATION'],
    optional: ['PUBLISHER_AGREEMENTS', 'QUALITY_CERTIFICATIONS'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      PUBLISHER_AGREEMENTS: 'Publisher Distribution Agreements',
      QUALITY_CERTIFICATIONS: 'Quality Assurance Certifications',
      INSURANCE_DOCUMENT: 'Public Liability Insurance (Optional)'
    }
  },
  ISLAMIC_SOUVENIRS: {
    required: ['BUSINESS_LICENSE', 'IDENTIFICATION'],
    optional: ['SUPPLIER_CONTRACTS', 'QUALITY_CERTIFICATIONS'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      SUPPLIER_CONTRACTS: 'Supplier Contracts',
      QUALITY_CERTIFICATIONS: 'Quality Assurance Certifications',
      INSURANCE_DOCUMENT: 'Public Liability Insurance (Optional)'
    }
  },
  // Provider types (legacy support)
  AMBULANCE_PROVIDER: {
    required: ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE', 'INSURANCE_DOCUMENT', 'IDENTIFICATION'],
    optional: ['OPERATING_PERMIT', 'VEHICLE_REGISTRATION'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      PROFESSIONAL_CERTIFICATE: 'Medical/Emergency Services Certification',
      INSURANCE_DOCUMENT: 'Vehicle and Liability Insurance',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      OPERATING_PERMIT: 'Transport Operating Permit',
      VEHICLE_REGISTRATION: 'Ambulance Vehicle Registration Documents'
    }
  },
  USTADZ: {
    required: ['PROFESSIONAL_CERTIFICATE', 'IDENTIFICATION'],
    optional: ['BUSINESS_LICENSE', 'SERVICE_CERTIFICATION'],
    descriptions: {
      PROFESSIONAL_CERTIFICATE: 'Islamic Studies Certification/Sijil',
      IDENTIFICATION: 'IC/Passport',
      BUSINESS_LICENSE: 'Business Registration (if applicable)',
      SERVICE_CERTIFICATION: 'Religious Service Authorization'
    }
  },
  CEMETERY_PROVIDER: {
    required: ['BUSINESS_LICENSE', 'INSURANCE_DOCUMENT'],
    optional: ['OPERATING_PERMIT', 'PROFESSIONAL_CERTIFICATE'],
    descriptions: {
      BUSINESS_LICENSE: 'Cemetery Operating License',
      INSURANCE_DOCUMENT: 'Public Liability Insurance',
      OPERATING_PERMIT: 'Land Use Permit',
      PROFESSIONAL_CERTIFICATE: 'Cemetery Management Certification'
    }
  },
  FUNERAL_HOME: {
    required: ['BUSINESS_LICENSE', 'PROFESSIONAL_CERTIFICATE', 'INSURANCE_DOCUMENT'],
    optional: ['OPERATING_PERMIT', 'SERVICE_CERTIFICATION'],
    descriptions: {
      BUSINESS_LICENSE: 'Funeral Home Business License',
      PROFESSIONAL_CERTIFICATE: 'Funeral Director Certification',
      INSURANCE_DOCUMENT: 'Professional Liability Insurance',
      OPERATING_PERMIT: 'Health Department Permit',
      SERVICE_CERTIFICATION: 'Mortuary Service License'
    }
  },
  VENDOR: {
    required: ['BUSINESS_LICENSE', 'IDENTIFICATION'],
    optional: ['PROFESSIONAL_CERTIFICATE', 'INSURANCE_DOCUMENT'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      PROFESSIONAL_CERTIFICATE: 'Professional Certification',
      INSURANCE_DOCUMENT: 'Public Liability Insurance'
    }
  },
  PROVIDER: {
    required: ['BUSINESS_LICENSE', 'IDENTIFICATION'],
    optional: ['PROFESSIONAL_CERTIFICATE', 'INSURANCE_DOCUMENT'],
    descriptions: {
      BUSINESS_LICENSE: 'Business Registration License',
      IDENTIFICATION: 'IC/Passport of Authorized Personnel',
      PROFESSIONAL_CERTIFICATE: 'Professional Certification',
      INSURANCE_DOCUMENT: 'Public Liability Insurance'
    }
  }
}

interface ProviderDocumentReviewPanelProps {
  document: ServiceProviderDocument
  onReviewComplete?: () => void
  onVerify?: (verificationNotes?: string) => void
  onReject?: (verificationNotes?: string) => void
  isVerifying?: boolean
  isRejecting?: boolean
}

export default function ProviderDocumentReviewPanel({
  document,
  onReviewComplete,
  onVerify,
  onReject,
  isVerifying = false,
  isRejecting = false
}: ProviderDocumentReviewPanelProps) {
  const [verificationNotes, setVerificationNotes] = useState('')
  const [showRejectForm, setShowRejectForm] = useState(false)
  const [showPreviewModal, setShowPreviewModal] = useState(false)

  const getStatusColor = (status: DocumentStatus): string => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'UPLOADED':
        return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'VERIFIED':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'REJECTED':
        return 'bg-red-100 text-red-800 border-red-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getStatusIcon = (status: DocumentStatus) => {
    switch (status) {
      case 'PENDING':
        return <ClockIcon className="h-4 w-4" />
      case 'UPLOADED':
        return <DocumentTextIcon className="h-4 w-4" />
      case 'VERIFIED':
        return <CheckCircleIcon className="h-4 w-4" />
      case 'REJECTED':
        return <XCircleIcon className="h-4 w-4" />
      default:
        return <DocumentTextIcon className="h-4 w-4" />
    }
  }

  const getStatusText = (status: DocumentStatus): string => {
    switch (status) {
      case 'PENDING':
        return 'Pending Review'
      case 'UPLOADED':
        return 'Uploaded'
      case 'VERIFIED':
        return 'Verified'
      case 'REJECTED':
        return 'Rejected'
      default:
        return status
    }
  }

  const getDocumentDescription = (serviceType: ServiceType, docType: string) => {
    const requirements = serviceDocumentRequirements[serviceType as keyof typeof serviceDocumentRequirements]
    return requirements?.descriptions[docType] || docType.replace(/_/g, ' ')
  }

  const getDocumentRequirement = (serviceType: ServiceType, docType: string) => {
    const requirements = serviceDocumentRequirements[serviceType as keyof typeof serviceDocumentRequirements]
    if (requirements?.required.includes(docType)) {
      return { type: 'required', color: 'text-red-600', bg: 'bg-red-50' }
    } else if (requirements?.optional.includes(docType)) {
      return { type: 'optional', color: 'text-blue-600', bg: 'bg-blue-50' }
    }
    return { type: 'unknown', color: 'text-gray-600', bg: 'bg-gray-50' }
  }

  const handleVerify = () => {
    if (isVerifying || isRejecting) return

    onVerify?.(verificationNotes.trim() || undefined)
    setVerificationNotes('')
  }

  const handleReject = () => {
    if (isVerifying || isRejecting) return

    if (!verificationNotes.trim()) {
      alert('Please provide rejection reason')
      return
    }

    onReject?.(verificationNotes.trim())
    setVerificationNotes('')
    setShowRejectForm(false)
  }

  const handleDownload = async () => {
    try {
      // Get the auth token from local storage
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('Authentication token not found')
      }

      // Fetch the document with proper authentication
      const response = await fetch(`${API_BASE_URL}/api/documents/provider/${document.id}/download`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to download document: ${response.status} ${response.statusText}`)
      }

      // Create blob and download link
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = document.originalName
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up blob URL
      URL.revokeObjectURL(url)
    } catch (error: any) {
      console.error('Download error:', error)
      // Fallback to opening in new window if download fails
      window.open(`${API_BASE_URL}/api/documents/provider/${document.id}/download`, '_blank')
    }
  }

  const isAlreadyReviewed = document.status === 'VERIFIED' || document.status === 'REJECTED'
  const requirement = getDocumentRequirement(document.serviceProvider.serviceType, document.type)

  return (
    <Card className="card">
      <CardHeader className="card-header">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <DocumentTextIcon className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <CardTitle className="card-title">{document.originalName}</CardTitle>
              <p className="text-sm text-neutral-500 mt-1">
                {getDocumentDescription(document.serviceProvider.serviceType, document.type)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(document.status)}`}>
              {getStatusIcon(document.status)}
              <span className="ml-1">{getStatusText(document.status)}</span>
            </span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${requirement.bg} ${requirement.color}`}>
              {requirement.type === 'required' ? (
                <><ShieldCheckIcon className="h-3 w-3 mr-1" /> Required</>
              ) : (
                <><DocumentCheckIcon className="h-3 w-3 mr-1" /> Optional</>
              )}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPreviewModal(true)}
              disabled={isVerifying || isRejecting}
              title="View document"
            >
              <EyeIcon className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              disabled={isVerifying || isRejecting}
              title="Download document"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="card-body">
        <div className="space-y-6">
          {/* Provider Info */}
          <div className="flex items-start gap-4">
            <div className="p-3 bg-neutral-100 rounded-lg">
              <BuildingOfficeIcon className="h-6 w-6 text-neutral-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-neutral-900 mb-1">
                {document.serviceProvider.companyName || `${document.serviceProvider.user.firstName} ${document.serviceProvider.user.lastName}`}
              </h3>
              <p className="text-sm text-neutral-500 mb-2">Provider ID: {document.serviceProvider.id}</p>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                document.serviceProvider.serviceType === 'AMBULANCE_PROVIDER' ? 'bg-blue-100 text-blue-800 border-blue-200' :
                document.serviceProvider.serviceType === 'USTADZ' ? 'bg-purple-100 text-purple-800 border-purple-200' :
                document.serviceProvider.serviceType === 'CEMETERY_PROVIDER' ? 'bg-green-100 text-green-800 border-green-200' :
                document.serviceProvider.serviceType === 'FUNERAL_HOME' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                'bg-gray-100 text-gray-800 border-gray-200'
              }`}>
                {document.serviceProvider.serviceType.replace(/_/g, ' ')}
              </span>
            </div>
          </div>

          {/* Document Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Document Type</label>
              <p className="text-sm text-neutral-900">
                {getDocumentDescription(document.serviceProvider.serviceType, document.type)}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Uploaded Date</label>
              <p className="text-sm text-neutral-900">{formatDate(document.uploadedAt)}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">File Size</label>
              <p className="text-sm text-neutral-900">{(document.fileSize / 1024).toFixed(1)} KB</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">File Type</label>
              <p className="text-sm text-neutral-900">{document.mimeType}</p>
            </div>
          </div>

          {/* Verification Notes */}
          {document.verificationNotes && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-amber-900">Verification Notes</h4>
                  <p className="text-sm text-amber-800 mt-1">{document.verificationNotes}</p>
                </div>
              </div>
            </div>
          )}

          {/* Admin Actions */}
          {!isAlreadyReviewed && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Review Notes <span className="text-red-500">*</span>
                </label>
                <Textarea
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.target.value)}
                  placeholder="Enter review notes for this document..."
                  rows={3}
                  className="w-full"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowRejectForm(true)}
                  disabled={isVerifying || isRejecting}
                  className="flex-1"
                >
                  <XCircleIcon className="h-4 w-4 mr-2" />
                  Reject Document
                </Button>

                <Button
                  variant="islamic"
                  onClick={handleVerify}
                  disabled={isVerifying || isRejecting}
                  className="flex-1"
                >
                  {isVerifying ? 'Verifying...' : (
                    <>
                      <CheckCircleIcon className="h-4 w-4 mr-2" />
                      Verify Document
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

            {/* Rejection Confirmation */}
          {showRejectForm && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-red-900">Confirm Document Rejection</h4>
                  <p className="text-sm text-red-700 mt-1">
                    This action will mark the document as rejected. The provider will need to upload a new document.
                  </p>
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowRejectForm(false)}
                  disabled={isVerifying || isRejecting}
                >
                  <XMarkIcon className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleReject}
                  disabled={isVerifying || isRejecting}
                >
                  {isRejecting ? 'Rejecting...' : (
                    <>
                      <XCircleIcon className="h-4 w-4 mr-2" />
                      Confirm Rejection
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Already Reviewed Status */}
          {isAlreadyReviewed && (
            <div className={`p-4 border rounded-lg ${
              document.status === 'VERIFIED'
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  document.status === 'VERIFIED'
                    ? 'bg-green-100'
                    : 'bg-red-100'
                }`}>
                  {document.status === 'VERIFIED' ? (
                    <CheckIcon className="h-5 w-5 text-green-600" />
                  ) : (
                    <XCircleIcon className="h-5 w-5 text-red-600" />
                  )}
                </div>
                <div className="flex-1">
                  <h4 className={`text-sm font-medium ${
                    document.status === 'VERIFIED'
                      ? 'text-green-900'
                      : 'text-red-900'
                  }`}>
                    Document {document.status.toLowerCase()}
                  </h4>
                  {document.verifiedAt && (
                    <p className={`text-sm mt-1 ${
                      document.status === 'VERIFIED'
                        ? 'text-green-700'
                        : 'text-red-700'
                    }`}>
                      Reviewed on {formatDate(document.verifiedAt)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Document Preview Modal */}
          <DocumentPreviewModal
            document={document}
            isOpen={showPreviewModal}
            onClose={() => setShowPreviewModal(false)}
          />
        </div>
      </CardContent>
    </Card>
  )
}